/**
 * State Badge Component
 * 
 * Displays clip lifecycle state with appropriate visual styling.
 */

import { ClipState } from '../../types/rbac.js';

/**
 * State badge configuration
 */
interface StateBadgeConfig {
  label: string;
  className: string;
  icon: string;
}

/**
 * State badge configurations
 */
const STATE_CONFIGS: Record<ClipState, StateBadgeConfig> = {
  [ClipState.Draft]: {
    label: 'Draft',
    className: 'badge-draft',
    icon: '📝'
  },
  [ClipState.TextReview]: {
    label: 'Text Review',
    className: 'badge-text-review',
    icon: '👀'
  },
  [ClipState.TextApproved]: {
    label: 'Text Approved',
    className: 'badge-text-approved',
    icon: '✅'
  },
  [ClipState.AudioQueued]: {
    label: 'Audio Queued',
    className: 'badge-audio-queued',
    icon: '⏳'
  },
  [ClipState.AudioReady]: {
    label: 'Audio Ready',
    className: 'badge-audio-ready',
    icon: '🔊'
  },
  [ClipState.AudioReview]: {
    label: 'Audio Review',
    className: 'badge-audio-review',
    icon: '🎧'
  },
  [ClipState.AudioApproved]: {
    label: 'Audio Approved',
    className: 'badge-audio-approved',
    icon: '✅'
  },
  [ClipState.Published]: {
    label: 'Published',
    className: 'badge-published',
    icon: '🚀'
  }
};

/**
 * Create a state badge element
 * 
 * @param state - Clip state
 * @returns HTML element
 */
export function createStateBadge(state: ClipState): HTMLElement {
  const config = STATE_CONFIGS[state];
  
  const badge = document.createElement('span');
  badge.className = `state-badge ${config.className}`;
  badge.setAttribute('role', 'status');
  badge.setAttribute('aria-label', `State: ${config.label}`);
  
  badge.innerHTML = `
    <span class="state-badge-icon" aria-hidden="true">${config.icon}</span>
    <span class="state-badge-label">${config.label}</span>
  `;
  
  return badge;
}

/**
 * Get state badge label
 * 
 * @param state - Clip state
 * @returns Label text
 */
export function getStateBadgeLabel(state: ClipState): string {
  return STATE_CONFIGS[state]?.label || state;
}

/**
 * Get state badge class name
 * 
 * @param state - Clip state
 * @returns CSS class name
 */
export function getStateBadgeClassName(state: ClipState): string {
  return STATE_CONFIGS[state]?.className || 'badge-default';
}

/**
 * Check if state is in review phase
 * 
 * @param state - Clip state
 * @returns true if in review
 */
export function isReviewState(state: ClipState): boolean {
  return state === ClipState.TextReview || state === ClipState.AudioReview;
}

/**
 * Check if state is approved
 * 
 * @param state - Clip state
 * @returns true if approved
 */
export function isApprovedState(state: ClipState): boolean {
  return state === ClipState.TextApproved || 
         state === ClipState.AudioApproved || 
         state === ClipState.Published;
}

/**
 * Get next possible states for transition
 * 
 * @param currentState - Current clip state
 * @returns Array of possible next states
 */
export function getNextStates(currentState: ClipState): ClipState[] {
  const transitions: Record<ClipState, ClipState[]> = {
    [ClipState.Draft]: [ClipState.TextReview],
    [ClipState.TextReview]: [ClipState.TextApproved, ClipState.Draft],
    [ClipState.TextApproved]: [ClipState.AudioQueued, ClipState.Draft],
    [ClipState.AudioQueued]: [], // System transitions to AudioReady
    [ClipState.AudioReady]: [ClipState.AudioReview],
    [ClipState.AudioReview]: [ClipState.AudioApproved, ClipState.TextApproved],
    [ClipState.AudioApproved]: [ClipState.Published, ClipState.TextApproved],
    [ClipState.Published]: [] // No transitions from published
  };
  
  return transitions[currentState] || [];
}

/**
 * Get state description for UI
 * 
 * @param state - Clip state
 * @returns Description text
 */
export function getStateDescription(state: ClipState): string {
  const descriptions: Record<ClipState, string> = {
    [ClipState.Draft]: 'Content is being created or edited',
    [ClipState.TextReview]: 'Awaiting text review and approval',
    [ClipState.TextApproved]: 'Text approved, ready for audio generation',
    [ClipState.AudioQueued]: 'Audio generation job in progress',
    [ClipState.AudioReady]: 'Audio generated, ready for review',
    [ClipState.AudioReview]: 'Awaiting audio review and approval',
    [ClipState.AudioApproved]: 'Audio approved, ready to publish',
    [ClipState.Published]: 'Live and available to users'
  };
  
  return descriptions[state] || 'Unknown state';
}
